import React, { Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lightbulb, GraduationCap } from 'lucide-react';
import { type ModuleId, MODULES, getModuleIntro, getModuleHexColor, getModuleIcon } from '../../content';
import { GroupId } from '../../config/systemConfig';
import { useFocusTrap } from '../../app/hooks/useFocusTrap';
import { useEscapeKey } from '../../app/hooks/useEscapeKey';
import { useBodyScrollLock } from '../../app/hooks/useBodyScrollLock';
interface ModuleIntroDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  moduleId: ModuleId;
  selectedGroup: GroupId;
}
export function ModuleIntroDrawer({
  isOpen,
  onClose,
  moduleId,
  selectedGroup
}: ModuleIntroDrawerProps) {
  const focusTrapRef = useFocusTrap(isOpen);
  useEscapeKey(onClose, isOpen);
  useBodyScrollLock(isOpen);
  const module = MODULES.find(m => m.id === moduleId);
  const moduleIntro = getModuleIntro(moduleId, selectedGroup);
  if (!module || !moduleIntro) return null;
  const IconComponent = getModuleIcon(module.icon);
  const moduleHex = getModuleHexColor(module.color);
  return <AnimatePresence data-id="element-1631">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40" data-id="element-1632" />

          {/* Bottom Sheet */}
          <motion.div ref={focusTrapRef} role="dialog" aria-modal="true" aria-labelledby="intro-title" initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 32,
        stiffness: 350
      }} className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl shadow-2xl max-h-[85vh] flex flex-col" data-id="element-1633">
            {/* Drag Handle */}
            <div className="flex justify-center pt-3 pb-1" onClick={onClose} data-id="element-1634">
              <div className="w-10 h-1 rounded-full bg-slate-300" data-id="element-1635" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-6 pb-4 border-b border-slate-100" data-id="element-1636">
              <div className="flex items-center gap-3" data-id="element-1637">
                <div className="p-2 rounded-xl shadow-sm border" style={{
              backgroundColor: `${moduleHex}15`,
              borderColor: `${moduleHex}30`,
              color: moduleHex
            }} data-id="element-1638">
                  <IconComponent className="w-6 h-6" strokeWidth={2.5} data-id="element-1639" />
                </div>
                <div data-id="element-1640">
                  <h2 id="intro-title" className="text-xl font-black text-slate-900 leading-tight" data-id="element-1641">
                    Wat leer je hier?
                  </h2>
                  <p className="text-xs text-slate-500 font-medium uppercase tracking-wider mt-0.5" data-id="element-1642">
                    {module.title}
                  </p>
                </div>
              </div>

              <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400 hover:text-slate-600" aria-label="Sluiten" data-id="element-1643">
                <X className="w-5 h-5" data-id="element-1644" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto overscroll-contain p-6" data-id="element-1645">
              <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100" data-id="element-1646">
                <div className="flex items-center gap-2 mb-4" data-id="element-1647">
                  <GraduationCap className="w-5 h-5 text-slate-400" data-id="element-1648" />
                  <h3 className="text-base font-bold text-slate-800" data-id="element-1649">
                    Onderwerpen in deze module
                  </h3>
                </div>

                <ul className="space-y-3" data-id="element-1650">
                  {moduleIntro.topics.map((topic, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-1651">
                      <div className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{
                  backgroundColor: moduleHex
                }} data-id="element-1652" />
                      <span className="text-sm font-medium text-slate-700 leading-relaxed" data-id="element-1653">
                        {topic}
                      </span>
                    </li>)}
                </ul>
              </div>

              {moduleIntro.tip && <div className="mt-4 bg-amber-50 border border-amber-100 rounded-2xl p-4 flex gap-3" data-id="element-1654">
                  <div className="shrink-0 mt-0.5" data-id="element-1655">
                    <div className="bg-white p-1.5 rounded-lg shadow-sm text-amber-500 border border-amber-100" data-id="element-1656">
                      <Lightbulb className="w-4 h-4" data-id="element-1657" />
                    </div>
                  </div>
                  <div data-id="element-1658">
                    <h4 className="font-bold text-amber-900 text-sm mb-1" data-id="element-1659">
                      Tip van Meester Djay
                    </h4>
                    <p className="text-sm font-medium text-amber-800/80 leading-relaxed" data-id="element-1660">
                      {moduleIntro.tip}
                    </p>
                  </div>
                </div>}

              {/* Bottom padding */}
              <div className="h-6" data-id="element-1661" />
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}